import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

/**
 * Robust API Client for Microsoft Graph API calls
 * Implements Microsoft's best practices for error handling, retry logic, and throttling
 */

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number; // Base delay in milliseconds
  maxDelay: number; // Maximum delay in milliseconds
  retryableStatuses: number[];
}

export interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  retry: RetryConfig;
}

export class RobustApiClient {
  private axiosInstance: AxiosInstance;
  private config: ApiClientConfig;

  constructor(config: Partial<ApiClientConfig> = {}) {
    this.config = {
      baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080',
      timeout: 30000, // 30 seconds
      retry: {
        maxRetries: 3,
        baseDelay: 1000, // 1 second
        maxDelay: 32000, // 32 seconds
        retryableStatuses: [429, 500, 502, 503, 504] // Retry on these HTTP status codes
      },
      ...config
    };

    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor with retry logic
    this.axiosInstance.interceptors.response.use(
      (response) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      async (error: AxiosError) => {
        console.error(`❌ API Error: ${error.response?.status} ${error.config?.url}`, error.message);
        
        const originalConfig = error.config as any;
        if (!originalConfig || originalConfig._retry) {
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const isRetryable = status && this.config.retry.retryableStatuses.includes(status);
        
        if (isRetryable) {
          return this.handleRetryableError(error, originalConfig);
        }

        return Promise.reject(error);
      }
    );
  }

  private async handleRetryableError(error: AxiosError, config: any): Promise<AxiosResponse> {
    const retryAttempt = config._retryCount || 0;
    
    if (retryAttempt >= this.config.retry.maxRetries) {
      console.error(`❌ Max retries (${this.config.retry.maxRetries}) exceeded for ${config.url}`);
      return Promise.reject(error);
    }

    config._retryCount = retryAttempt + 1;
    config._retry = true;

    const delay = this.calculateRetryDelay(error, retryAttempt);
    console.warn(`🔄 Retrying API call (${config._retryCount}/${this.config.retry.maxRetries}) after ${delay}ms: ${config.url}`);
    
    await this.sleep(delay);
    return this.axiosInstance(config);
  }

  private calculateRetryDelay(error: AxiosError, retryAttempt: number): number {
    // Check for Retry-After header (Microsoft Graph throttling)
    const retryAfter = error.response?.headers['retry-after'];
    if (retryAfter) {
      const retryAfterMs = parseInt(retryAfter) * 1000; // Convert seconds to milliseconds
      console.log(`⏰ Using Retry-After header: ${retryAfterMs}ms`);
      return Math.min(retryAfterMs, this.config.retry.maxDelay);
    }

    // Exponential backoff with jitter
    const exponentialDelay = this.config.retry.baseDelay * Math.pow(2, retryAttempt);
    const jitter = Math.random() * 0.1 * exponentialDelay; // Add 10% jitter
    const delay = exponentialDelay + jitter;
    
    return Math.min(delay, this.config.retry.maxDelay);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  public setAuthToken(token: string | null) {
    if (token) {
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('🔐 Auth token set');
    } else {
      delete this.axiosInstance.defaults.headers.common['Authorization'];
      console.log('🔓 Auth token cleared');
    }
  }

  /**
   * Clears authentication tokens and performs logout cleanup
   * @param notifyBackend - Whether to notify the backend about the logout
   */
  public async clearAuthToken(notifyBackend: boolean = false) {
    try {
      // Clear the auth token from axios instance
      delete this.axiosInstance.defaults.headers.common['Authorization'];
      console.log('🔓 Auth token cleared from API client');

      // Optionally notify backend about logout
      if (notifyBackend) {
        try {
          console.log('🔄 Notifying backend about logout...');
          await this.post('/api/auth/logout', {}, {
            timeout: 5000, // Short timeout for logout notification
            validateStatus: (status) => status < 500 // Accept any non-server error
          });
          console.log('✅ Backend notified about logout');
        } catch (backendError) {
          console.warn('⚠️ Failed to notify backend about logout:', backendError);
          // Don't fail the entire logout process if backend notification fails
        }
      }
    } catch (error) {
      console.error('❌ Error during auth token cleanup:', error);
      // Still clear the token even if there are errors
      delete this.axiosInstance.defaults.headers.common['Authorization'];
    }
  }

  /**
   * Performs complete logout cleanup including token clearing and backend notification
   */
  public async logout() {
    console.log('🚪 API Client logout initiated');
    await this.clearAuthToken(true);
    console.log('✅ API Client logout completed');
  }

  // HTTP method wrappers with enhanced error handling
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.get<T>(url, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error as AxiosError, 'GET', url);
    }
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.post<T>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error as AxiosError, 'POST', url);
    }
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.put<T>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error as AxiosError, 'PUT', url);
    }
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.delete<T>(url, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error as AxiosError, 'DELETE', url);
    }
  }

  private enhanceError(error: AxiosError, method: string, url: string): Error {
    const status = error.response?.status;
    const message = error.response?.data || error.message;
    
    // Create enhanced error message
    let enhancedMessage = `API ${method} ${url} failed`;
    if (status) {
      enhancedMessage += ` with status ${status}`;
    }
    if (message) {
      enhancedMessage += `: ${message}`;
    }

    // Add specific guidance based on error type
    if (status === 401) {
      enhancedMessage += ' (Authentication required - please check your access token)';
    } else if (status === 403) {
      enhancedMessage += ' (Insufficient permissions - check Microsoft Graph permissions)';
    } else if (status === 404) {
      enhancedMessage += ' (Resource not found - verify the endpoint URL and resource ID)';
    } else if (status === 429) {
      enhancedMessage += ' (Rate limited - Microsoft Graph is throttling requests)';
    }

    const enhancedError = new Error(enhancedMessage);
    (enhancedError as any).originalError = error;
    (enhancedError as any).status = status;
    (enhancedError as any).url = url;
    (enhancedError as any).method = method;

    return enhancedError;
  }
}

// Create a singleton instance for the application
export const apiClient = new RobustApiClient();

// Export utility functions for specific API patterns
export const GraphApiHelpers = {
  /**
   * Build a Microsoft Graph API URL with proper query parameters
   */
  buildGraphUrl: (resource: string, options: {
    select?: string[];
    filter?: string;
    orderBy?: string;
    top?: number;
    skip?: number;
  } = {}) => {
    const params = new URLSearchParams();
    
    if (options.select?.length) {
      params.append('$select', options.select.join(','));
    }
    if (options.filter) {
      params.append('$filter', options.filter);
    }
    if (options.orderBy) {
      params.append('$orderby', options.orderBy);
    }
    if (options.top) {
      params.append('$top', options.top.toString());
    }
    if (options.skip) {
      params.append('$skip', options.skip.toString());
    }

    return params.toString() ? `${resource}?${params.toString()}` : resource;
  },

  /**
   * Handle paginated responses from Microsoft Graph
   */
  handlePagination: async <T>(
    initialResponse: any,
    fetchNext: (nextLink: string) => Promise<any>
  ): Promise<T[]> => {
    const allItems: T[] = [...(initialResponse.value || [])];
    let nextLink = initialResponse['@odata.nextLink'];

    while (nextLink) {
      try {
        const nextResponse = await fetchNext(nextLink);
        allItems.push(...(nextResponse.value || []));
        nextLink = nextResponse['@odata.nextLink'];
      } catch (error) {
        console.error('❌ Error fetching next page:', error);
        break; // Stop pagination on error but return what we have
      }
    }

    return allItems;
  },

  /**
   * Safely encode URL path parameters (like task IDs with special characters)
   */
  encodePathParam: (param: string): string => {
    return encodeURIComponent(param);
  }
};
